import { runInAction } from "mobx"
import { Address } from "viem"

import type { ClFarmApiTransport } from "@/domains/cl-farm/cl-farm-api-transport"
import type { ClFarmLensInput, ClFarmOnChainTransport } from "@/domains/cl-farm/cl-farm-on-chain-transport"
import type { ClFarmRepository } from "@/domains/cl-farm/cl-farm-repository"
import type { ClFarmApiData } from "@/domains/cl-farm/types"
import type { IGaugeRepository } from "@/domains/gauge"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenRepository } from "@/domains/tokens"
import type { WalletConnectionStore } from "@/domains/wallet"
import { convertApiResponseToClFarmStoreDAO } from "@/domains/cl-farm/cl-farm-converter"
import { earnedAbi } from "@/domains/cl-farm/cl-farm-on-chain-transport"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"
import { GaugeAddedToRepositoryEvent } from "@/domains/gauge/events"
import { OrchestratedQuery } from "@/domains/library"
import { LogStore } from "@/domains/log/log-store"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

export class ClFarmOrchestrator {
  private loadFarmsQuery: OrchestratedQuery<Array<ClFarmApiData>> | undefined
  private userInfoQuery: OrchestratedQuery<any[]> | undefined
  private earnedRewardsQuery: OrchestratedQuery<Array<{ farmAddress: Address; earned: bigint }>> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private gaugeAddedHandler: ((event: GaugeAddedToRepositoryEvent) => void) | undefined

  constructor(
    private readonly clFarmApiTransport: ClFarmApiTransport,
    private readonly clFarmOnChainTransport: ClFarmOnChainTransport,
    private readonly clFarmRepository: ClFarmRepository,
    private readonly tokenRepository: TokenRepository,
    private readonly walletStore: WalletConnectionStore,
    private readonly log: LogStore,
    private readonly eventBus: IEventBus,
    private readonly gaugeRepository: IGaugeRepository | undefined,
    private readonly governanceTokenAddress: LcAddress,
  ) {}

  initialize(
    loadFarmsQuery: OrchestratedQuery<Array<ClFarmApiData>>,
    userInfoQuery?: OrchestratedQuery<any[]>,
    earnedRewardsQuery?: OrchestratedQuery<Array<{ farmAddress: Address; earned: bigint }>>,
  ) {
    this.loadFarmsQuery = loadFarmsQuery
    this.userInfoQuery = userInfoQuery
    this.earnedRewardsQuery = earnedRewardsQuery

    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to gauge added events to attempt farm connections
    this.gaugeAddedHandler = this.handleGaugeAdded.bind(this)
    this.eventBus.subscribe(GaugeAddedToRepositoryEvent.TYPE, this.gaugeAddedHandler)
  }

  private async handleWalletConnect(event: WalletConnectedEvent): Promise<void> {
    // this.log.info("ClFarmOrchestrator.handleWalletConnect")
    // this.log.info("ClFarmOrchestrator.userInfoQuery", this.userInfoQuery?.executionCount, this.userInfoQuery)
    // this.log.info("ClFarmOrchestrator.clFarmRepository.totalFarms", this.clFarmRepository.totalFarms)

    if (this.userInfoQuery) {
      this.userInfoQuery.forceExecute()
    }

    if (this.earnedRewardsQuery) {
      this.earnedRewardsQuery.forceExecute()
    }
  }

  private handleWalletDisconnect(event: WalletDisconnectedEvent): void {
    // this.log.info("ClFarmOrchestrator.handleWalletDisconnect")
    this.clFarmRepository.clearAllUserValues()
  }

  get query(): OrchestratedQuery<Array<ClFarmApiData>> | undefined {
    return this.loadFarmsQuery
  }

  get userInfoQueryInstance(): OrchestratedQuery<any[]> | undefined {
    return this.userInfoQuery
  }

  /**
   * Load farms from API (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadFarmsTrigger(_query: IOrchestratedQuerySender<Array<ClFarmApiData>>): Promise<Array<ClFarmApiData>> {
    // this.log.info("ClFarmOrchestrator.onLoadFarmsTrigger totalFarms", this.clFarmRepository.totalFarms)
    return await this.clFarmApiTransport.loadClFarms()
  }

  /**
   * Callback for when farms query succeeds
   */
  onLoadFarmsQuerySuccess(_sender: IOrchestratedQuerySender<Array<ClFarmApiData>>, data: Array<ClFarmApiData>): void {
    try {
      runInAction(() => {
        for (let i = 0; i < data.length; i++) {
          const element = data[i]
          if (element) {
            this.processFarmFromAPI(element)
          }
        }
      })

      // this.log.info("ClFarmOrchestrator.onLoadFarmsQuerySuccess totalFarms", this.clFarmRepository.totalFarms)

      if (this.userInfoQuery && this.userInfoQuery.executionCount === 0) {
        // this.log.info("ClFarmOrchestrator.onLoadFarmsQuerySuccess forcing query to start")
        this.userInfoQuery?.start()
      } else if (this.userInfoQuery) {
        // this.log.info("ClFarmOrchestrator.onLoadFarmsQuerySuccess forceExecute")
        this.userInfoQuery?.forceExecute()
      }
    } catch (error) {
      console.error("Error processing farm data:", error)
    }
  }

  /**
   * Callback for when farms query fails
   */
  onLoadFarmsQueryFailed(_sender: IOrchestratedQuerySender<Array<ClFarmApiData>>, error: Error): void {
    console.error("Failed to load CL farms", error)
  }

  /**
   * Load user info from on-chain (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadUserInfoTrigger(
    query: IOrchestratedQuerySender<Array<any>>,
    walletStore: WalletConnectionStore,
  ): Promise<Array<any>> {
    // If no connected address, use safe address, but ensure it's valid
    const safeAddress = walletStore.safeLensAddress
    if (!walletStore.connectedAddress && (!safeAddress || typeof safeAddress !== "string")) {
      // Return empty array if we don't have a valid address to query
      return []
    }
    const who = walletStore.connectedAddress ?? new LcAddress(safeAddress)
    const farms = this.clFarmRepository.getAll()

    const inputsMap = new Map<Address, ClFarmLensInput>()
    // loop over inputs and add them to the map if the return value from getLensParams is not undefined
    farms.forEach((x) => {
      const lensParams = x.getLensParams(who)
      if (lensParams) {
        inputsMap.set(x.address.toViemAddress(), [
          lensParams[0].toViemAddress(),
          lensParams[1].toViemAddress(),
          lensParams[2].toViemAddress(),
        ])
      }
    })

    // console.log("who", who)
    // console.log("walletStore.connectedAddress", walletStore.connectedAddress)

    // this.log.info("ClFarmOrchestrator.onLoadUserInfoTrigger", who)

    // now create inputs from the map
    const inputs = Array.from(inputsMap.values())

    const userInfoData = await this.clFarmOnChainTransport.loadClFarmData(inputs, query, 25)

    // now we map it back to the farm address
    const mappedResults = Array.from(inputsMap.keys()).map((key, index) => {
      return {
        A: key,
        N: userInfoData[index],
      }
    })

    return mappedResults
  }

  /**
   * Callback for when user info query succeeds
   */
  onLoadUserInfoQuerySuccess(_sender: IOrchestratedQuerySender<any[]>, data: Array<any>): void {
    try {
      runInAction(() => {
        // this.log.info("ClFarmOrchestrator.onLoadUserInfoQuerySuccess", data.length)
        // console.log("Loaded user info data:", data)
        for (const item of data) {
          this.processUserInfoFromOnChain(item)
        }
      })
    } catch (error) {
      console.error("Error processing user info data:", error)
    }
  }

  /**
   * Callback for when user info query fails
   */
  onLoadUserInfoQueryFailed(_sender: IOrchestratedQuerySender<any[]>, error: Error): void {
    console.error("❌ ClFarmOrchestrator: Failed to load user info", error)
  }

  /**
   * Process user info data and add to repository
   */
  private processUserInfoFromOnChain(data: any): void {
    const farm = this.clFarmRepository.find(new LcAddress(data.A))
    if (farm) {
      farm.updateLensStaticData(data.N)
    }
  }

  /**
   * Load earned rewards from on-chain (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadEarnedRewardsTrigger(
    _query: IOrchestratedQuerySender<Array<{ farmAddress: Address; earned: bigint }>>,
    walletStore: WalletConnectionStore,
  ): Promise<Array<{ farmAddress: Address; earned: bigint }>> {
    if (!walletStore.connectedAddress) {
      return []
    }

    const farms = this.clFarmRepository.getAll()
    const account = walletStore.connectedAddress

    // Only fetch for farms that have a gauge and farm address
    const farmsWithGauges = farms.filter((farm) => farm.gaugeAddress && farm.farmAddress)

    if (farmsWithGauges.length === 0) {
      return []
    }

    // Create multicall contracts
    const contracts = farmsWithGauges.map((farm) => ({
      address: farm.farmAddress.toViemAddress(),
      abi: earnedAbi,
      functionName: "earned" as const,
      args: [this.governanceTokenAddress.toViemAddress(), account.toViemAddress()] as [Address, Address],
    }))

    const results = await this.clFarmOnChainTransport.loadClFarmEarnedRewards(contracts)

    // Map results back to farm addresses
    return farmsWithGauges.map((farm, index) => {
      const result = results[index]
      return {
        farmAddress: farm.address.toViemAddress(),
        earned: result?.status === "success" ? (result.result ?? 0n) : 0n,
      }
    })
  }

  /**
   * Callback for when earned rewards query succeeds
   */
  onLoadEarnedRewardsQuerySuccess(
    _sender: IOrchestratedQuerySender<Array<{ farmAddress: Address; earned: bigint }>>,
    data: Array<{ farmAddress: Address; earned: bigint }>,
  ): void {
    try {
      runInAction(() => {
        for (const item of data) {
          const farm = this.clFarmRepository.find(new LcAddress(item.farmAddress))
          if (farm) {
            farm.onChainUserGaugeGovernanceEarned = item.earned
          }
        }
      })
    } catch (error) {
      console.error("Error processing earned rewards data:", error)
    }
  }

  /**
   * Callback for when earned rewards query fails
   */
  onLoadEarnedRewardsQueryFailed(
    _sender: IOrchestratedQuerySender<Array<{ farmAddress: Address; earned: bigint }>>,
    error: Error,
  ): void {
    console.error("❌ ClFarmOrchestrator: Failed to load earned rewards", error)
  }

  /**
   * Process farm data from API and add to repository
   */
  private processFarmFromAPI(apiData: ClFarmApiData): void {
    const found = this.clFarmRepository.find(new LcAddress(apiData.integration.equalizerPair.address))

    if (found) {
      // Update existing farm - convert API data to DAO format first
      const farmStoreDAO = convertApiResponseToClFarmStoreDAO(apiData, this.tokenRepository)
      found.updateStaticData(farmStoreDAO)
      return
    }

    // Convert API data to store DAO
    const farmStoreDAO = convertApiResponseToClFarmStoreDAO(apiData, this.tokenRepository)

    // Create new farm store
    const farm = new ClFarmStore(farmStoreDAO)

    // Add to repository
    this.clFarmRepository.add(farm)

    // Attempt to connect to gauge if gauge address exists
    this.attemptFarmToGaugeConnection(farm)
  }

  /**
   * Handle gauge added event - attempt to connect farms to the new gauge
   */
  private handleGaugeAdded(event: GaugeAddedToRepositoryEvent): void {
    // When a gauge is added, check if any farms can connect to it
    const farms = this.clFarmRepository.getAll()
    let matchedFarms = 0
    for (const farm of farms) {
      if (farm.gaugeAddress?.equals(event.gaugeAddress)) {
        matchedFarms++
        this.attemptFarmToGaugeConnection(farm)
      }
    }

    // Only log if we expected to find farms but didn't
    // (This is informational - gauges can exist without farms)
  }

  /**
   * Attempt to connect a farm to its gauge
   */
  private attemptFarmToGaugeConnection(farm: ClFarmStore): void {
    if (!farm.gaugeAddress) {
      // Farm has no gauge - this is normal for farms without gauges, don't log
      return
    }

    if (!this.gaugeRepository) {
      // console.warn(
      //   `[CL-FARM-GAUGE-CONNECTION] ❌ Farm ${farm.address.toString()} (${farm.displayName}) - gaugeRepository not available`,
      // )
      return
    }

    const gauge = this.gaugeRepository.find(farm.gaugeAddress)
    if (!gauge) {
      // Gauge not found - check if gauges have been loaded
      // Only warn if gauges exist in the repository (meaning they've been loaded)
      // but this specific gauge is missing
      const allGauges = this.gaugeRepository.getAll()
      if (allGauges.length > 0) {
        // Gauges have been loaded, but this gauge is missing - this is a data quality issue
        // console.warn(
        //   `[CL-FARM-GAUGE-CONNECTION] ⚠️ Farm ${farm.address.toString()} (${farm.displayName}) references gauge ${farm.gaugeAddress.toString()}, but this gauge does not exist in the API response. This may indicate missing gauge data in the API.`,
        // )
      }
      // If no gauges exist yet, they just haven't loaded - this is expected, don't warn
      return
    }

    if (farm.connectedGauge) {
      // Already connected - nothing to do
      return
    }

    // Connection successful - no need to log
    farm.connectToGauge(gauge)
  }
}
